import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { CALL_RECORDS } from '../../api/endpoints';
import { getLeads } from '../../store/slices/leadsSlice';
import { extractList } from '../../utils/response';
import { getErrorMessage } from '../../utils/errorHandler';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { CALL_RECORDS_LIST_PATH } from '../../navigations/path';
import { buildLeadsAutocompleteParams } from '../../components/Leads/utils';
import { normalizeLeadOption, loadCallRecordStatuses } from './callRecordFormMeta';
import CallRecordingUploadSection from './CallRecordingUploadSection';
import {
  uploadRecordingAttachmentFileChunked,
  extractAttachmentIdFromUploadResponse,
} from './callRecordAttachmentUpload';
import { isAudioFile } from './recordingFileTypes';

const multipartHeaders = { headers: { 'Content-Type': 'multipart/form-data' } };

const makeRowKey = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `r-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const CreateCallRecord = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [leadInput, setLeadInput] = useState('');
  const [leadOptions, setLeadOptions] = useState([]);
  const [leadLoading, setLeadLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [statusId, setStatusId] = useState('');
  const [note, setNote] = useState('');
  /** Each row: local key, file, attachment id after upload, uploading flag */
  const [recordingItems, setRecordingItems] = useState([]);

  const prevLeadIdRef = useRef(undefined);

  useEffect(() => {
    const id = selectedLead?.id ?? selectedLead?.uuid;
    if (prevLeadIdRef.current !== undefined && id !== prevLeadIdRef.current) {
      setRecordingItems([]);
    }
    prevLeadIdRef.current = id;
  }, [selectedLead?.id, selectedLead?.uuid]);

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        setLoadingMeta(true);
        const list = await loadCallRecordStatuses(dispatch);
        setStatusOptions(list);
        if (!list.length) {
          toast.warn('No statuses found for recordings. Check model name on /statuses (CallRecord) or permissions.');
        }
      } catch (err) {
        toast.error(err?.message || 'Failed to load statuses');
        setStatusOptions([]);
      } finally {
        setLoadingMeta(false);
      }
    };
    loadStatuses();
  }, [dispatch]);

  const fetchLeads = useCallback(
    async (searchTerm) => {
      setLeadLoading(true);
      try {
        const params = buildLeadsAutocompleteParams({
          search: searchTerm,
        });
        const res = await dispatch(getLeads(params)).unwrap();
        const raw = extractList(res);
        const list = (Array.isArray(raw) ? raw : []).map(normalizeLeadOption).filter(Boolean);
        setLeadOptions(list);
      } catch (err) {
        toast.error(getErrorMessage(err) || 'Failed to load leads');
        setLeadOptions([]);
      } finally {
        setLeadLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const t = setTimeout(() => fetchLeads(leadInput), 350);
    return () => clearTimeout(t);
  }, [leadInput, fetchLeads]);

  const handleRecordingFiles = async (files) => {
    const list = Array.isArray(files) ? files : Array.from(files || []);
    const audioFiles = list.filter(isAudioFile);
    if (!audioFiles.length) {
      toast.warn('Please choose audio files (e.g. MP3, WAV, M4A).');
      return;
    }
    const leadKey = selectedLead?.id ?? selectedLead?.uuid;
    if (!leadKey) {
      toast.warn('Select a lead before uploading recordings.');
      return;
    }

    let uploaded = 0;
    for (const file of audioFiles) {
      const key = makeRowKey();
      setRecordingItems((prev) => [...prev, { key, file, attachmentId: null, uploading: true }]);
      try {
        const uploadBody = await uploadRecordingAttachmentFileChunked(file, { modelId: leadKey });
        const attachmentId = extractAttachmentIdFromUploadResponse(uploadBody);
        if (!attachmentId) {
          toast.error(`Upload finished but no attachment id for "${file.name}".`);
          setRecordingItems((prev) => prev.filter((r) => r.key !== key));
          continue;
        }
        setRecordingItems((prev) =>
          prev.map((r) =>
            r.key === key ? { ...r, attachmentId: String(attachmentId), uploading: false } : r
          )
        );
        uploaded += 1;
      } catch (err) {
        toast.error(
          getErrorMessage(err) || err.response?.data?.message || `Upload failed: ${file.name}`
        );
        setRecordingItems((prev) => prev.filter((r) => r.key !== key));
      }
    }
    if (uploaded > 0) {
      toast.success(uploaded === 1 ? 'Recording uploaded.' : `${uploaded} recordings uploaded.`);
    }
  };

  const handleRemoveRecordingRow = (key) => {
    setRecordingItems((prev) => prev.filter((r) => r.key !== key));
  };

  const attachmentIdsReady = recordingItems.filter((r) => r.attachmentId).map((r) => r.attachmentId);
  const anyUploading = recordingItems.some((r) => r.uploading);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const leadKey = selectedLead?.id ?? selectedLead?.uuid;
    if (!leadKey) {
      toast.warn('Select a lead');
      return;
    }
    if (!statusId) {
      toast.warn('Select a status');
      return;
    }
    if (!attachmentIdsReady.length) {
      toast.warn('Upload at least one recording and wait for attachment ids.');
      return;
    }
    if (anyUploading) {
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('lead_id', String(leadKey));
      fd.append('status_id', String(statusId));
      fd.append('note', note || '');
      attachmentIdsReady.forEach((id, i) => {
        fd.append(`recordings[${i}]`, String(id));
      });

      await api.post(CALL_RECORDS.CREATE, fd, multipartHeaders);
      toast.success('Recording saved.');
      navigate(CALL_RECORDS_LIST_PATH);
    } catch (err) {
      toast.error(getErrorMessage(err) || err.response?.data?.message || 'Failed to save recording');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
          Upload recording
        </Typography>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  options={leadOptions}
                  loading={leadLoading}
                  filterOptions={(x) => x}
                  getOptionLabel={(opt) => opt?.label || opt?.name || opt?.email || ''}
                  isOptionEqualToValue={(a, b) => (a?.id ?? a?.uuid) === (b?.id ?? b?.uuid)}
                  value={selectedLead}
                  onChange={(_, v) => setSelectedLead(v)}
                  onInputChange={(_, v) => setLeadInput(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Lead"
                      placeholder="Search leads by name, email…"
                      required
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" required disabled={loadingMeta}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={statusId}
                    onChange={(e) => setStatusId(e.target.value)}
                  >
                    {statusOptions.map((s) => {
                      const val = s.id ?? s.uuid;
                      return (
                        <MenuItem key={String(val)} value={String(val)}>
                          {s.name ?? s.title ?? s.label ?? String(val)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Note"
                  multiline
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Recording note…"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <CallRecordingUploadSection
          multiple
          items={recordingItems}
          onFilesSelected={(list) => {
            void handleRecordingFiles(list);
          }}
          onRemoveItem={handleRemoveRecordingRow}
          uploadingAttachment={anyUploading}
          saving={submitting}
          disabled={submitting}
        />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
          <Button
            type="button"
            variant="contained"
            disabled={
              submitting ||
              anyUploading ||
              !attachmentIdsReady.length
            }
            onClick={handleSubmit}
          >
            {submitting ? <CircularProgress size={22} /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" onClick={() => navigate(CALL_RECORDS_LIST_PATH)}>
            Cancel
          </Button>
        </Box>
      </Container>
    </PageLayout>
  );
};

export default CreateCallRecord;
